<template>
    <Form as="" :validation-schema="schema" v-slot="{ meta, handleSubmit }">
        <bs5-base-modal :title="$t('users.editTitle')" :visible="visible" @close="closeModal">
            <template v-slot:default>
                <form novalidate @submit.prevent="handleSubmit($event, updateUser)">
                    <bs5-form-field id="editUserFullName" name="fullName" icon="bi-person" autocomplete="name"
                        :label="$t('users.fullName')" v-model="user.fullName" />
                    <bs5-form-field id="editUserEmail" name="email" type="email" icon="bi-envelope" autocomplete="off"
                        :label="$t('users.email')" v-model="user.email" />
                    <bs5-form-field id="editUserRole" name="role" :label="$t('users.role')" :options="roleChoices"
                        v-model="user.role" />
                    <button type="submit" class="d-none" tabindex="-1" aria-hidden="true"></button>
                </form>
            </template>

            <template v-slot:footer>
                <bs5-cancel-button @click="closeModal" />
                <bs5-save-button :loading="saving" :disabled="!meta.valid" @click="handleSubmit($event, updateUser)">
                    <i class="bi bi-check2"></i>{{ $t("common.save") }}
                </bs5-save-button>
            </template>
        </bs5-base-modal>
    </Form>
</template>

<script lang="ts">
import { t } from "@/helpers/i18n";
import { defineComponent, ref } from "vue";
import { Form } from "vee-validate";
import * as yup from "yup";
import userService from "@/services/user.service";
import { User } from "@/models/user.model";
import { UserRole } from "@/enums/user-role.enum";
import { showToast } from "@/utils/toast";
import { requiredChoice, requiredEmail, requiredText } from "@/utils/validation";

export default defineComponent({
    name: "EditUserModal",
    components: { Form },
    props: {
        visible: { type: Boolean, required: true },
        userId: { type: String, required: true },
    },
    emits: ["close", "user-updated"],
    data() {
        return {
            user: ref<User>({
                id: "",
                fullName: "",
                email: "",
                role: UserRole.Client, // Domyślnie Client (jako liczba)
            }),
            saving: ref(false), // Stan ładowania
            roleOptions: [
                { value: UserRole.SuperAdmin, labelKey: "roles.SuperAdmin" },
                { value: UserRole.Distributor, labelKey: "roles.Distributor" },
                { value: UserRole.Client, labelKey: "roles.Client" },
            ],
        };
    },
    computed: {
        schema() {
            return yup.object({
                fullName: requiredText(256),
                email: requiredEmail(),
                role: requiredChoice(),
            });
        },
        roleChoices(): { value: number; label: string }[] {
            return this.roleOptions.map(role => ({ value: role.value, label: t(role.labelKey) }));
        },
    },
    watch: {
        userId: {
            immediate: true,
            handler(newId) {
                if (newId) {
                    this.fetchUser();
                }
            },
        },
    },
    methods: {
        async fetchUser() {
            try {
                const response = await userService.getUserById(this.userId);
                this.user = response;
            } catch (error) {
                console.error("Error fetching user:", error);
            }
        },
        async updateUser() {
            this.saving = true;
            try {
                await userService.updateUser(this.user.id as string, {
                    fullName: this.user.fullName,
                    email: this.user.email,
                    role: this.user.role,
                });
                this.$emit("user-updated");
                showToast(t("users.updated"), "success");
                this.closeModal();
            } catch (error) {
                showToast(t("users.updateFailed"), "error");
                console.error("Error updating user:", error);
            } finally {
                this.saving = false;
            }
        },
        closeModal() {
            this.$emit("close");
        },
    },
    mounted() {
        if (this.userId) {
            this.fetchUser();
        }
    },
});
</script>
